<template>
  <div class="upRegion" :class="{ 'upRegion--large': large, 'upRegion--hovered': hovered }" @mouseenter="$emit('hover', region.id)" @mouseleave="$emit('hover', null)">
    <!-- Übersicht: Foto | Upscale | Überarbeitung groß, eng auf das Gesicht gezoomt -->
    <div v-if="large" class="upRegion__images">
      <!-- Synchroner Zoom über alle drei Bilder: Mausrad zoomt, Ziehen verschiebt, Knöpfe rechts oben -->
      <div class="upRegion__zoomCtl" @click.stop>
        <button type="button" title="Verkleinern" :disabled="zoomLevel <= 1" @click="setZoom(zoomLevel / 1.4)"><v-icon small>mdi-magnify-minus-outline</v-icon></button>
        <span class="upRegion__zoomVal" title="Zurücksetzen" @click="setZoom(1)">{{ Math.round(zoomLevel * 100) }} %</span>
        <button type="button" title="Vergrößern (auch Mausrad über dem Bild)" :disabled="zoomLevel >= 8" @click="setZoom(zoomLevel * 1.4)"><v-icon small>mdi-magnify-plus-outline</v-icon></button>
      </div>
      <figure v-if="region.crops.original" class="upRegion__fig">
        <div class="upRegion__frame" :style="frameStyle" :class="{ 'upRegion__frame--zoomed': zoomLevel > 1 }" @wheel="onZoomWheel" @mousedown="onPanStart">
          <div class="upRegion__zoom" :style="zoomStyle">
            <img :src="region.crops.original" alt="" :style="photoStyle" draggable="false" />
          </div>
        </div>
        <figcaption>Foto</figcaption>
      </figure>
      <figure v-else-if="region.noPhotoMatch" class="upRegion__fig">
        <div class="upRegion__frame upRegion__frame--empty" :style="frameStyle">
          <span><v-icon small>mdi-image-off-outline</v-icon><br />kein Gegenstück im Foto gefunden</span>
        </div>
        <figcaption>Foto</figcaption>
      </figure>
      <figure v-if="region.crops.before" class="upRegion__fig">
        <div class="upRegion__frame" :style="frameStyle" :class="{ 'upRegion__frame--zoomed': zoomLevel > 1 }" @wheel="onZoomWheel" @mousedown="onPanStart">
          <div class="upRegion__zoom" :style="zoomStyle">
            <img :src="region.crops.before" alt="" :style="cropStyle" draggable="false" @load="onCropLoad" />
          </div>
        </div>
        <figcaption>Upscale <b :style="{ color: color(region.scoreBefore) }">{{ show(region.scoreBefore) }}</b></figcaption>
      </figure>
      <!-- Ohne Überarbeitung bleibt der dritte Rahmen bewusst leer (sonst sähe man den Upscale doppelt) -->
      <figure v-if="!region.crops.after" class="upRegion__fig">
        <div class="upRegion__frame upRegion__frame--empty" :style="frameStyle">
          <span v-if="!editRunning" class="upRegion__emptyText"><v-icon small class="mr-1">mdi-check-circle-outline</v-icon>Keine Überarbeitung nötig</span>
          <div v-if="editRunning" class="upRegion__running"><v-progress-circular indeterminate size="22" width="3" color="#fff" /></div>
        </div>
        <figcaption>unverändert</figcaption>
      </figure>
      <figure v-else class="upRegion__fig">
        <!-- Upscale unten, Überarbeitung mit der Regler-Deckkraft darüber -->
        <div class="upRegion__frame" :style="frameStyle" :class="{ 'upRegion__frame--zoomed': zoomLevel > 1 }" @wheel="onZoomWheel" @mousedown="onPanStart">
          <div class="upRegion__zoom" :style="zoomStyle">
            <img v-if="region.crops.before" :src="region.crops.before" alt="" :style="cropStyle" draggable="false" />
            <img :src="region.crops.after" alt="" :style="[cropStyle, { opacity: region.adjustable ? region.opacity : 1 }]" class="upRegion__top" draggable="false" />
          </div>
          <!-- Versionen des Bereichs (Detail-Pass + Region-Edits): Kacheln rechts unten, Klick setzt die Version ins Endbild -->
          <div v-if="versions.length > 1" class="upRegion__versions" @click.stop>
            <button
                v-for="(v, i) in versions"
                :key="i"
                type="button"
                class="upRegion__version"
                :class="{ 'upRegion__version--active': i === region.activeVersion }"
                :disabled="editBusy || i === region.activeVersion"
                :title="`${v.label}${v.score !== null && v.score !== undefined ? ' · Score ' + v.score : ''}${v.instruction ? ' · ' + v.instruction : ''}${v.warning ? ' · PRÜFEN: ' + v.warning : ''}`"
                @click="$emit('version', { id: region.id, index: i })"
            >
              <img v-if="v.entry && v.entry.after" :src="v.entry.after" alt="" />
              <span>{{ versionTag(v, i) }}</span>
              <i v-if="v.warning" class="upRegion__versionWarn">!</i>
            </button>
          </div>
          <div v-if="editRunning" class="upRegion__running"><v-progress-circular indeterminate size="22" width="3" color="#fff" /></div>
        </div>
        <figcaption>
          {{ percent < 100 ? `Überarbeitung ${percent} %` : "Überarbeitung" }}
          <b v-if="region.scoreAfter !== null" :style="{ color: color(region.scoreAfter) }">{{ region.scoreAfter }}</b>
          <span v-if="region.warning" class="upRegion__warnText" :title="region.warning"><v-icon x-small>mdi-alert</v-icon> Prüfen: {{ region.warning }}</span>
          <span v-if="geometryLabel" class="upRegion__muted" :title="geometryTitle">{{ geometryLabel }}</span>
          <!-- Ebenen-Modus (Malte 30.10.2026): Render verschieben/skalieren/drehen/radieren — RegionLayerEditor im LiveEditor -->
          <button v-if="editable && region.rect && (region.crops.after || region.suggestion)" type="button" class="upRegion__dl upRegion__dl--layer" :disabled="editBusy" title="Ebene bearbeiten: Gesicht verschieben, skalieren, drehen, weich radieren, Deckkraft" @mouseenter="$emit('layer-prefetch', region)" @click.stop="$emit('layer', region)">
            <v-icon x-small>mdi-cursor-move</v-icon> Ebene
          </button>
          <button v-if="rawRenderUrl" type="button" class="upRegion__dl" :disabled="downloadingRaw" title="Rohrender herunterladen: Modellausgabe vor Ausrichtung, Farbangleich und Einsetzen" @click.stop="downloadRaw">
            <v-progress-circular v-if="downloadingRaw" indeterminate size="10" width="2" />
            <v-icon v-else x-small>mdi-download</v-icon> Rohrender
          </button>
        </figcaption>
      </figure>
    </div>

    <!-- Liste: ein kleines Mischbild, Klick öffnet den Gesichtsvergleich -->
    <figure v-else class="upRegion__thumb" title="Vorher / Nachher groß ansehen" @click="$emit('focus', region)">
      <div class="upRegion__frame" :style="frameStyle">
        <img v-if="region.crops.before" :src="region.crops.before" alt="" loading="lazy" :style="cropStyle" @load="onCropLoad" />
        <img v-if="region.crops.after" :src="region.crops.after" alt="" loading="lazy" class="upRegion__top" :style="[cropStyle, { opacity: region.adjustable ? region.opacity : 1 }]" />
      </div>
    </figure>

    <div class="upRegion__body">
      <div class="upRegion__head">
        <span class="upRegion__dot" :style="{ background: color(region.currentScore) }"></span>
        <b class="upRegion__label" :title="region.label">{{ region.label }}</b>
        <span v-if="!large" class="upRegion__score">
          <span :style="{ color: color(region.scoreBefore) }">{{ show(region.scoreBefore) }}</span>
          <template v-if="region.scoreAfter !== null">
            <span class="upRegion__arrow">→</span>
            <b :style="{ color: color(region.scoreAfter), opacity: region.opacity < 0.5 ? 0.45 : 1 }">{{ region.scoreAfter }}</b>
          </template>
        </span>
      </div>

      <!-- Regler: wie stark die Überarbeitung des Detail-Pass eingeblendet wird -->
      <div v-if="region.adjustable" class="upRegion__slider" :class="{ 'upRegion__slider--off': !enabled }">
        <span class="upRegion__end" title="Nur Upscale" @click="set(0)">Upscale</span>
        <input type="range" min="0" max="100" step="5" :value="percent" :disabled="!enabled" @input="set($event.target.value / 100)" @dblclick="set(1)" />
        <span class="upRegion__end" title="Überarbeitung voll" @click="set(1)">{{ percent }} %</span>
      </div>
      <div v-else-if="region.skipped" class="upRegion__muted">{{ shortSkip }}</div>

      <!-- Neu rendern: Wunsch-Text (Hinweise als Vorschläge), Referenz Foto/Artwork -->
      <div v-if="editable" class="upRegion__edit">
        <template v-if="!editOpen">
          <!-- kleine Karte: Formular in der großen Ansicht (Vergleich) öffnen — dort ist Platz -->
          <button type="button" class="upRegion__editBtn" :disabled="editBusy" :title="editBusy ? 'Ein Region-Edit läuft bereits' : 'Diesen Bereich neu rendern lassen'" @click.stop="large ? (editOpen = true) : $emit('edit', region)">
            <v-progress-circular v-if="editRunning" indeterminate size="10" width="2" class="mr-1" />
            <v-icon v-else x-small left>mdi-autorenew</v-icon>{{ editRunning ? "Wird gerendert …" : "Neu rendern" }}
          </button>
          <span v-if="region.edited" class="upRegion__editedTag" title="Dieser Bereich wurde nachträglich neu gerendert">bearbeitet</span>
        </template>
      </div>

      <div v-if="hints.length" class="upRegion__hintsToggle" @click="open = !open">
        <v-icon x-small>{{ open ? "mdi-chevron-down" : "mdi-chevron-right" }}</v-icon>
        {{ hints.length }} {{ hints.length === 1 ? "Hinweis" : "Hinweise" }}
      </div>
      <ul v-if="open && hints.length" class="upRegion__hints">
        <li v-for="(h, i) in hints" :key="i">{{ h }}</li>
      </ul>
    </div>

    <!-- Region-Edit: volle Breite unter den Bildern (nur große Ansicht) -->
    <div v-if="editable && editOpen" class="upEdit" @click.stop>
      <div class="upEdit__head">
        <div>
          <div class="upEdit__title">{{ region.label }} neu rendern</div>
          <div class="upEdit__sub">Grundlage ist immer der aktuelle Ausschnitt aus dem Upscale, das Bild „Überarbeitung“. Ergebnis in 1–2 Minuten, etwa 5–10 Cent.</div>
        </div>
        <button type="button" class="upEdit__close" title="Schließen" @click="editOpen = false"><v-icon small>mdi-close</v-icon></button>
      </div>

      <div class="upEdit__grid">
        <section class="upEdit__section">
          <div class="upEdit__label">Was ans Modell geschickt wird <span class="upEdit__optional">anklicken zum An-/Abwählen</span></div>
          <div class="upEdit__sends">
            <!-- Grundlage: Überarbeitung (aktueller Stand) und/oder reiner Upscale — mindestens eins -->
            <button v-if="region.crops.after" type="button" class="upEdit__send" :class="{ 'upEdit__send--on': editInputs.current }" title="Aktueller Stand nach Detail-Pass bzw. Edits" @click="toggleBase('current')">
              <img :src="region.crops.after" alt="" />
              <div><b>Überarbeitung</b><span>{{ editInputs.current ? (editInputs.upscale ? "aktueller Stand · wird neu gezeichnet, bestimmt Stil, Farben und Lage" : "aktueller Stand · bestimmt Stil, Farben und Lage") : "wird nicht mitgeschickt" }}</span></div>
              <v-icon small :color="editInputs.current ? '#2e9e5b' : '#bbb'">{{ editInputs.current ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}</v-icon>
            </button>
            <button type="button" class="upEdit__send" :class="{ 'upEdit__send--on': editInputs.upscale, 'upEdit__send--fixed': !region.crops.after }" :title="region.crops.after ? 'Reiner Upscale vor dem Detail-Pass' : 'Immer dabei: der Bereich, der neu gezeichnet wird'" @click="region.crops.after && toggleBase('upscale')">
              <img v-if="region.crops.before" :src="region.crops.before" alt="" />
              <div><b>Upscale</b><span>{{ !region.crops.after ? "dieser Bereich, aktueller Stand · bestimmt Stil, Farben und Lage" : !editInputs.upscale ? "wird nicht mitgeschickt" : editInputs.current ? "vor dem Detail-Pass · zusätzliche Vorlage für Formen und Details" : "vor dem Detail-Pass · wird neu gezeichnet, bestimmt Stil, Farben und Lage" }}</span></div>
              <v-icon small :color="editInputs.upscale ? '#2e9e5b' : '#bbb'">{{ editInputs.upscale ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}</v-icon>
            </button>
            <button type="button" class="upEdit__send" :class="{ 'upEdit__send--on': editInputs.photo }" :disabled="!region.crops.original" :title="region.crops.original ? 'Kundenfoto mitschicken' : 'Kein Kundenfoto vorhanden'" @click="editInputs.photo = !editInputs.photo">
              <img v-if="region.crops.original" :src="region.crops.original" alt="" />
              <div v-else class="upEdit__sendIcon"><v-icon small>mdi-image-off-outline</v-icon></div>
              <div><b>Kundenfoto-Ausschnitt</b><span>{{ !region.crops.original ? "kein Foto vorhanden" : editInputs.photo ? "dieselbe Stelle im Foto · Vorlage für Gesicht, Buchstaben, Details" : "wird nicht mitgeschickt" }}</span></div>
              <v-icon small :color="editInputs.photo ? '#2e9e5b' : '#bbb'">{{ editInputs.photo ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}</v-icon>
            </button>
            <button type="button" class="upEdit__send upEdit__send--noimg" :class="{ 'upEdit__send--on': editInputs.artwork }" title="Komplettes Artwork als Stilvorlage mitschicken" @click="editInputs.artwork = !editInputs.artwork">
              <div class="upEdit__sendIcon"><v-icon small>mdi-palette-outline</v-icon></div>
              <div><b>Original-Artwork</b><span>{{ editInputs.artwork ? "komplett, nur als Stilvorlage, nicht als Motiv" : "wird nicht mitgeschickt" }}</span></div>
              <v-icon small :color="editInputs.artwork ? '#2e9e5b' : '#bbb'">{{ editInputs.artwork ? "mdi-check-circle" : "mdi-checkbox-blank-circle-outline" }}</v-icon>
            </button>
          </div>
          <p class="upEdit__hint">
            Jedes Bild wird der KI mit Erklärung übergeben. Du kannst dich im Wunsch darauf beziehen: „wie im Foto", „wie im Upscale", „wie im Artwork".
            {{ editInputs.photo
                ? "Mit Foto: Das Motiv soll dem Foto entsprechen, Stil, Farben und Lage bleiben wie im Upscale. Für Gesichter, Tiere, Kennzeichen."
                : "Ohne Foto: Das Modell zeichnet den Ausschnitt nur nach sich selbst und deinem Wunsch neu. Für Text, Logos oder Stellen, die es im Foto nicht gibt." }}
          </p>
        </section>

        <section class="upEdit__section">
          <div class="upEdit__label">Dein Wunsch <span class="upEdit__optional">optional</span></div>
          <textarea v-model="editText" rows="3" class="upEdit__text" placeholder="z. B. Augen offen wie im Foto · Schriftzug lautet MAX · Hut weglassen"></textarea>
          <template v-if="hints.length">
            <div class="upEdit__label upEdit__label--mt">Hinweise der Bewertung <span class="upEdit__optional">Klick übernimmt den Punkt in den Wunsch</span></div>
            <ul class="upEdit__hints">
              <li v-for="(h, i) in hints" :key="i" @click="addHint(h)">
                <v-icon x-small class="mr-1">mdi-plus-circle-outline</v-icon><span>{{ h }}</span>
              </li>
            </ul>
          </template>
        </section>
      </div>

      <div class="upEdit__footer">
        <button type="button" class="upEdit__btn upEdit__btn--go" :disabled="editBusy" @click="submitEdit"><v-icon small left color="#fff">mdi-autorenew</v-icon>Jetzt rendern</button>
        <button type="button" class="upEdit__btn" @click="editOpen = false">Abbrechen</button>
        <span class="upEdit__meta">1–2 Minuten · ca. 5–10 Cent · der alte Stand bleibt als Version erhalten</span>
      </div>
    </div>
  </div>
</template>

<script>
import { scoreColor } from "@/upscaleJobs";
import { downloadFileFromUrl } from "@/utils";

/** Rand um die erkannte Box beim engen Zuschnitt (Anteil der Boxgröße je Seite) */
const TIGHT_MARGIN = 0.22;

/**
 * Eine Region der Upscale-Prüfung: Vorher/Nachher, Score, Deckkraft-Regler der Überarbeitung,
 * Hinweise eingeklappt. `large` = Zeile im Gesichtsvergleich (Foto | Upscale | Überarbeitung).
 * `tight` zoomt die Ausschnitte eng auf die erkannte Box — die gespeicherten Ausschnitte
 * enthalten viel Umgebung, das Gesicht selbst wäre sonst klein.
 */
export default {
  name: "UpscaleRegionCard",
  props: {
    region: { type: Object, required: true },
    /** Präfix für Download-Dateinamen, z. B. die Bestellnummer */
    downloadPrefix: { type: String, default: "" },
    limit: { type: Number, default: 85 },
    /** Regler bedienbar (nur in der Detail-Pass-Version) */
    enabled: { type: Boolean, default: true },
    large: { type: Boolean, default: false },
    /** Mausrad über dem Bild zoomt (sonst nur mit Strg/⌘, damit Listen scrollbar bleiben) */
    wheelZoom: { type: Boolean, default: true },
    tight: { type: Boolean, default: true },
    hovered: { type: Boolean, default: false },
    /** „Neu rendern" anbieten (nur in der Prüfliste, nicht im Vergleichsdialog) */
    editable: { type: Boolean, default: false },
    /** Ein Region-Edit läuft gerade (irgendeine Region) */
    editBusy: { type: Boolean, default: false },
    /** Dieser Bereich wird gerade gerendert */
    editRunning: { type: Boolean, default: false },
    /** Formular direkt aufgeklappt öffnen (große Ansicht nach Klick auf der kleinen Karte) */
    editInitiallyOpen: { type: Boolean, default: false },
  },
  data() {
    const crops = this.region.crops || {};
    // Ohne Überarbeitung ist der Upscale der aktuelle Stand → fest gewählt
    return { open: false, naturalRatio: null, editOpen: this.editInitiallyOpen, editText: "", editInputs: { current: !!crops.after, upscale: !crops.after, photo: !!crops.original, artwork: true }, zoomLevel: 1, pan: { x: 0, y: 0 }, panDrag: null, downloadingRaw: false };
  },
  computed: {
    percent() {
      return Math.round((this.region.opacity === undefined ? 1 : this.region.opacity) * 100);
    },
    versions() {
      return this.region.versions || [];
    },
    /** Sichtfenster im Ausschnitt (0…1): die Box plus Rand; ohne Lage-Info die Mitte */
    view() {
      if (!this.tight) return { x: 0, y: 0, w: 1, h: 1 };
      const inner = this.region.inner;
      if (!inner || !(inner.w > 0) || !(inner.h > 0)) return { x: 0.2, y: 0.2, w: 0.6, h: 0.6 };
      const w = Math.min(1, inner.w * (1 + 2 * TIGHT_MARGIN));
      const h = Math.min(1, inner.h * (1 + 2 * TIGHT_MARGIN));
      const x = Math.min(1 - w, Math.max(0, inner.x - inner.w * TIGHT_MARGIN));
      const y = Math.min(1 - h, Math.max(0, inner.y - inner.h * TIGHT_MARGIN));
      return { x, y, w, h };
    },
    /** Seitenverhältnis des Sichtfensters in Pixeln (Ausschnitt ist nicht quadratisch) */
    /** Gemeinsamer Zoom + Verschiebung für Foto, Upscale und Überarbeitung (große Ansicht) */
    zoomStyle() {
      return { transform: `translate(${this.pan.x}%, ${this.pan.y}%) scale(${this.zoomLevel})` };
    },
    frameStyle() {
      // Nicht überarbeitete Regionen haben kein rect → Seitenverhältnis des geladenen Ausschnitts
      const ratio = this.region.cropRatio || this.naturalRatio || 1;
      return { aspectRatio: `${Math.max(0.05, this.view.w * ratio)} / ${Math.max(0.05, this.view.h)}` };
    },
    /** Upscale-/Überarbeitungs-Ausschnitt so legen, dass das Sichtfenster den Rahmen füllt */
    cropStyle() {
      const v = this.view;
      return { position: "absolute", width: 100 / v.w + "%", height: 100 / v.h + "%", left: (-v.x / v.w) * 100 + "%", top: (-v.y / v.h) * 100 + "%", maxWidth: "none" };
    },
    /** Foto-Ausschnitt: eigene Geometrie, die Box liegt dort mittig → um den gleichen Faktor zur Mitte zoomen */
    photoStyle() {
      if (!this.tight) return { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" };
      const zoom = Math.min(2.4, Math.max(1, 1 / Math.max(this.view.w, this.view.h)));
      return { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transform: `scale(${zoom})` };
    },
    /** Überwiegend ausgeblendet → Hinweise des Upscales, sonst die der Überarbeitung */
    hints() {
      const r = this.region;
      return (r.adjustable && r.opacity < 0.5 ? r.deviationsBefore : r.deviations) || [];
    },
    /** Landmarken-Ausrichtung des Renders (Maßstab, Drehung, Rest) — nur Gesichter mit Detail-Pass */
    geometryLabel() {
      const g = this.region && this.region.geometry
      if (!g) return ""
      if (g.source !== "landmarks") return "Ausrichtung: Kanten (kein Gesicht im Ausschnitt erkannt)"
      const scale = Number(g.scale)
      const rot = Number(g.rotDeg)
      const rest = Number(g.residualPct)
      return `Ausrichtung ×${Number.isFinite(scale) ? scale.toFixed(2) : "?"} · ${Number.isFinite(rot) ? rot.toFixed(1) : "?"}° · Rest ${Number.isFinite(rest) ? rest.toFixed(1) : "?"} %`
    },
    geometryTitle() {
      const g = this.region && this.region.geometry
      if (!g || g.source !== "landmarks") return ""
      return `Render gegenüber Artwork-Gesicht vor der Korrektur: Maßstab ×${g.scale}, Drehung ${g.rotDeg}°, Versatz ${g.dx}/${g.dy} px, Landmarken-Abstand ${g.rawPct} % der Gesichtsbreite (${g.faceWidth} px) · nach Ausrichtung ${g.residualPct} %`
    },
    /** Rohausgabe des Modells für die aktive Version (Region-Edit) bzw. den Detail-Pass */
    rawRenderUrl() {
      const versions = this.region.versions || [];
      const active = versions[this.region.activeVersion];
      return (active && active.entry && active.entry.rendered) || (this.region.crops && this.region.crops.rendered) || null;
    },
    shortSkip() {
      const text = String(this.region.skipped || "");
      return /kein Detail-Pass nötig/.test(text) ? "Gut genug — nicht überarbeitet" : text;
    },
  },
  methods: {
    async downloadRaw() {
      if (!this.rawRenderUrl || this.downloadingRaw) return;
      this.downloadingRaw = true;
      try {
        const name = [this.downloadPrefix, String(this.region.label || this.region.id).replace(/\s+/g, "-"), "rohrender"].filter(Boolean).join("_");
        await downloadFileFromUrl(this.rawRenderUrl, `${name}.auto`);
      } finally {
        this.downloadingRaw = false;
      }
    },
    onCropLoad(e) {
      const img = e && e.target;
      if (img && img.naturalWidth && img.naturalHeight) this.naturalRatio = img.naturalWidth / img.naturalHeight;
    },
    set(value) {
      if (!this.enabled) return;
      this.$emit("opacity", { id: this.region.id, value });
    },
    /** Kurzlabel der Versions-Kachel: UP = Upscale (kein Detail-Pass), DP = Detail-Pass, E1… = Edits */
    versionTag(v, i) {
      if (v.label === "Detail-Pass") return "DP";
      if (v.label === "Upscale") return "UP";
      // Manuelle Ebenen-Versionen (layerEdit.ts) zählen eigen: M1, M2 …
      if (v.manual) return "M" + this.versions.slice(0, i + 1).filter((x) => x.manual).length;
      const baseCount = this.versions.slice(0, i).filter((x) => x.label === "Detail-Pass" || x.label === "Upscale" || x.manual).length;
      return "E" + (i - baseCount + 1);
    },
    setZoom(level) {
      const z = Math.max(1, Math.min(8, Number(level) || 1));
      this.zoomLevel = Math.round(z * 100) / 100;
      if (this.zoomLevel === 1) this.pan = { x: 0, y: 0 };
      else this.clampPan();
    },
    onZoomWheel(e) {
      // In Listen scrollt das Rad; Zoom nur mit Strg/⌘ (Trackpad-Pinch sendet ctrlKey) oder in der Einzelansicht
      if (!this.wheelZoom && !e.ctrlKey && !e.metaKey && this.zoomLevel <= 1) return;
      e.preventDefault();
      this.setZoom(this.zoomLevel * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
    },
    /** Verschieben bei Zoom > 1: Mausweg in Prozent der Rahmenbreite, auf alle drei Bilder gleich */
    onPanStart(e) {
      if (this.zoomLevel <= 1 || e.button !== 0) return;
      e.preventDefault();
      const frame = e.currentTarget.getBoundingClientRect();
      this.panDrag = { x: e.clientX, y: e.clientY, px: this.pan.x, py: this.pan.y, w: frame.width || 1, h: frame.height || 1 };
      const move = (ev) => {
        const d = this.panDrag;
        if (!d) return;
        this.pan = { x: d.px + ((ev.clientX - d.x) / d.w) * 100, y: d.py + ((ev.clientY - d.y) / d.h) * 100 };
        this.clampPan();
      };
      const up = () => {
        this.panDrag = null;
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", up);
      };
      window.addEventListener("mousemove", move);
      window.addEventListener("mouseup", up);
    },
    /** Nicht über den Bildrand hinaus schieben */
    clampPan() {
      const max = ((this.zoomLevel - 1) / 2) * 100;
      this.pan = { x: Math.max(-max, Math.min(max, this.pan.x)), y: Math.max(-max, Math.min(max, this.pan.y)) };
    },
    /** Grundlage umschalten — eine von beiden bleibt immer gewählt */
    toggleBase(key) {
      const other = key === "current" ? "upscale" : "current";
      if (this.editInputs[key] && !this.editInputs[other]) return;
      this.editInputs[key] = !this.editInputs[key];
    },
    submitEdit() {
      this.$emit("rerender", { region: { source: this.region.source || "existing", id: this.region.id, label: this.region.label, type: this.region.type }, instruction: this.editText.trim(), inputs: { ...this.editInputs }, referenceMode: this.editInputs.photo ? "photo" : "artwork" });
      this.editOpen = false;
    },
    /** Hinweis als Wunsch übernehmen (Umkehrung: „Kinn zu breit" → „Kinn wie im Foto") */
    addHint(h) {
      const short = this.shortHint(h);
      this.editText = (this.editText ? this.editText.trim() + "; " : "") + `${short} — wie im Foto`;
    },
    shortHint(h) {
      return String(h).split(/[—:]/)[0].trim().slice(0, 40);
    },
    show(v) {
      return v === null || v === undefined ? "–" : v;
    },
    color(score) {
      return scoreColor(score, this.limit);
    },
  },
};
</script>

<style lang="scss" scoped>
.upRegion__dl--layer {
  color: #6a4fc4;
  border-color: rgba(106, 79, 196, 0.35);
}
.upRegion__dl {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.14);
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  cursor: pointer;
  &:hover { background: #edeafa; color: #6a4fc4; border-color: #edeafa; }
  &:disabled { opacity: 0.6; cursor: default; }
}
.upRegion {
  display: flex;
  gap: 10px;
  padding: 8px;
  border-radius: 12px;
  background: #f6f7f9;
  border: 1px solid transparent;
  transition: border-color 0.12s, box-shadow 0.12s, background 0.12s;

  &--hovered {
    background: #fff;
    border-color: #7b5cd6;
    box-shadow: 0 2px 10px rgba(123, 92, 214, 0.18);
  }
  &__frame--empty {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f1f2f6;
    color: rgba(0, 0, 0, 0.5);
    font-size: 11.5px;
    text-align: center;
    padding: 8px;
  }
  &__frame {
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    background: #f4f3f7;
    width: 100%;

    &--zoomed {
      cursor: grab;
    }
    &--empty {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f4f3f7;
      border: 1px dashed rgba(0, 0, 0, 0.12);
    }
    &--zoomed:active {
      cursor: grabbing;
    }
  }
  &__emptyText {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);

    .v-icon {
      color: #3f9d68 !important;
    }
  }
  &__zoom {
    position: absolute;
    inset: 0;
    transform-origin: center center;
    will-change: transform;
  }
  &__zoomCtl {
    position: absolute;
    top: -4px;
    right: 0;
    z-index: 4;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 4px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);

    button {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      &:hover {
        background: rgba(0, 0, 0, 0.06);
      }
      &:disabled {
        opacity: 0.35;
        cursor: default;
      }
    }
  }
  &__zoomVal {
    min-width: 42px;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
  }
  &__top {
    transition: opacity 0.08s linear;
  }
  &__thumb {
    flex: 0 0 64px;
    margin: 0;
    cursor: zoom-in;
  }
  &__images {
    position: relative;
    display: flex;
    gap: 12px;
  }
  &__fig {
    flex: 1;
    min-width: 0;
    margin: 0;
    text-align: center;
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.6);

    figcaption {
      margin-top: 6px;
    }
  }
  &__body {
    flex: 1;
    min-width: 0;
    font-size: 12px;
  }
  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  &__dot {
    flex: 0 0 8px;
    height: 8px;
    border-radius: 50%;
  }
  &__label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__score {
    white-space: nowrap;
    font-weight: 700;
  }
  &__arrow {
    margin: 0 3px;
    color: rgba(0, 0, 0, 0.4);
  }
  &__slider {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;

    input[type="range"] {
      flex: 1;
      min-width: 0;
      accent-color: #7b5cd6;
      cursor: pointer;
    }
    &--off {
      opacity: 0.45;
    }
  }
  &__end {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    white-space: nowrap;
    min-width: 34px;

    &:last-child {
      text-align: right;
      font-weight: 700;
      color: #6a4fc4;
    }
  }
  &__versions {
    position: absolute;
    right: 6px;
    bottom: 6px;
    display: flex;
    gap: 4px;
    z-index: 2;
  }
  &__versionWarn {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 15px;
    height: 15px;
    border-radius: 50%;
    background: #d8493f;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    font-style: normal;
    line-height: 15px;
    text-align: center;
    box-shadow: 0 0 0 1.5px #fff;
  }
  &__warnText {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: #c62828;
    white-space: normal;

    .v-icon {
      color: #c62828 !important;
    }
  }
  &__version {
    position: relative;
    width: 46px;
    height: 46px;
    padding: 0;
    border-radius: 6px;
    overflow: hidden;
    border: 2px solid rgba(255, 255, 255, 0.85);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
    background: #333;
    cursor: pointer;
    display: block;

    img {
      position: static;
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      opacity: 1;
    }
    span {
      position: absolute;
      left: 0;
      bottom: 0;
      padding: 0 4px;
      font-size: 9px;
      font-weight: 700;
      color: #fff;
      background: rgba(0, 0, 0, 0.55);
      border-top-right-radius: 4px;
    }
    &--active {
      border-color: #7b5cd6;
      box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.5);
      cursor: default;
    }
    &:disabled:not(&--active) {
      opacity: 0.5;
      cursor: default;
    }
  }
  &__running {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.25);
    z-index: 3;
  }
  &__edit {
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  &__editBtn {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-size: 11px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.65);
    cursor: pointer;

    &--go {
      background: #7b5cd6;
      border-color: #7b5cd6;
      color: #fff;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
  &__editedTag {
    font-size: 10px;
    font-weight: 700;
    color: #7b5cd6;
  }
  &__muted {
    margin-top: 4px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__hintsToggle {
    margin-top: 4px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;
    user-select: none;
  }
  &__hints {
    margin: 2px 0 0;
    padding-left: 18px;
    font-size: 11px;
    line-height: 1.35;
    color: rgba(0, 0, 0, 0.65);
  }

  /* Gesichtsvergleich: eine Region je Zeile, drei große Bilder, Bedienung darunter */
  &--large {
    flex-direction: column;
    gap: 12px;
    padding: 14px;
    border-radius: 14px;

    .upRegion__body {
      font-size: 14px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px 18px;
    }
    .upRegion__head {
      flex: 0 0 auto;
    }
    .upRegion__label {
      flex: 0 0 auto;
    }
    .upRegion__slider {
      flex: 1 1 260px;
      margin-top: 0;
    }
    .upRegion__muted,
    .upRegion__hintsToggle {
      margin-top: 0;
    }
    .upRegion__hints {
      flex: 1 1 100%;
    }
    .upRegion__end {
      font-size: 12px;
    }
  }
}

/* Region-Edit: volle Breite, zweispaltig (links: was gesendet wird · rechts: Wunsch + Hinweise) */
.upEdit {
  width: 100%;
  margin-top: 4px;
  padding: 14px 16px 12px;
  border-radius: 12px;
  border: 1px solid rgba(123, 92, 214, 0.35);
  background: linear-gradient(180deg, #fbfaff 0%, #fff 100%);
  font-size: 12px;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }
  &__title {
    font-size: 15px;
    font-weight: 800;
  }
  &__sub {
    margin-top: 2px;
    color: rgba(0, 0, 0, 0.55);
  }
  &__close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }
  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 18px;
  }
  &__section {
    min-width: 0;
  }
  &__label {
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 6px;

    &--mt {
      margin-top: 12px;
    }
  }
  &__optional {
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
    color: rgba(0, 0, 0, 0.4);
    margin-left: 6px;
  }
  &__tabs {
    display: inline-flex;
    padding: 3px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.06);
    margin-bottom: 10px;
  }
  &__tab {
    padding: 5px 14px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.55);
    cursor: pointer;

    &--on {
      background: #fff;
      color: #282828;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
    }
  }
  &__sends {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__send {
    display: grid;
    grid-template-columns: 44px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: #fff;
    opacity: 0.55;
    transition: opacity 0.15s;
    width: 100%;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &--fixed {
      cursor: default;
    }
    &:disabled {
      cursor: default;
    }

    &--on {
      opacity: 1;
      border-color: rgba(46, 158, 91, 0.35);
    }
    img,
    .upEdit__sendIcon {
      width: 44px;
      height: 44px;
      border-radius: 6px;
      object-fit: cover;
      background: #f0eef6;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    b {
      display: block;
      font-size: 12px;
    }
    span {
      display: block;
      font-size: 11px;
      color: rgba(0, 0, 0, 0.55);
    }
  }
  &__hint {
    margin: 8px 0 0;
    font-size: 11.5px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.6);
  }
  &__text {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    font-size: 12.5px;
    resize: vertical;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #7b5cd6;
      box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.18);
    }
  }
  &__hints {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 150px;
    overflow: auto;

    li {
      display: flex;
      align-items: flex-start;
      gap: 2px;
      padding: 4px 6px;
      border-radius: 6px;
      font-size: 11.5px;
      line-height: 1.35;
      color: rgba(0, 0, 0, 0.7);
      cursor: pointer;

      &:hover {
        background: rgba(123, 92, 214, 0.08);
      }
    }
  }
  &__footer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
  &__btn {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.7);
    cursor: pointer;

    &--go {
      background: #7b5cd6;
      border-color: #7b5cd6;
      color: #fff;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
  &__meta {
    margin-left: auto;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
  }
}
</style>
